/**
 * Research History Redux Slice
 * Manages state for /sellers-agents/analysis/history endpoint
 * Uses offset-based pagination with infinite scroll (skip/limit)
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import {
    ResearchHistoryApiService,
    type ResearchHistoryItem,
    type ResearchHistoryParams,
} from '../api/researchHistoryApi';

const DEFAULT_LIMIT = 15;

// ── Filters ───────────────────────────────────────────────────────────────────

export interface ResearchHistoryFilters {
    sort: 'asc' | 'desc';
    period_start?: string;
    period_end?: string;
    only_favourites?: boolean;
    search?: string;
    marketplace?: string;
}

// ── State ─────────────────────────────────────────────────────────────────────

export interface ResearchHistoryState {
    items: ResearchHistoryItem[];
    total: number;
    filters: ResearchHistoryFilters;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    hasMore: boolean;
}

const initialState: ResearchHistoryState = {
    items: [],
    total: 0,
    filters: { sort: 'desc' },
    loading: false,
    loadingMore: false,
    error: null,
    hasMore: true,
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchResearchHistory = createAsyncThunk<
    { items: ResearchHistoryItem[]; total: number },
    void,
    { state: RootState; rejectValue: string }
>(
    'researchHistory/fetch',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { filters } = getState().researchHistory;
            const params: ResearchHistoryParams = {
                ...filters,
                skip: 0,
                limit: DEFAULT_LIMIT,
            };
            const response = await ResearchHistoryApiService.fetchHistory(params);
            return { items: response.items, total: response.total };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch research history';
            return rejectWithValue(message);
        }
    }
);

export const fetchMoreResearchHistory = createAsyncThunk<
    { items: ResearchHistoryItem[]; total: number },
    void,
    { state: RootState; rejectValue: string }
>(
    'researchHistory/fetchMore',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { filters, items, hasMore } = getState().researchHistory;
            if (!hasMore) return rejectWithValue('No more data');

            const params: ResearchHistoryParams = {
                ...filters,
                skip: items.length,
                limit: DEFAULT_LIMIT,
            };
            const response = await ResearchHistoryApiService.fetchHistory(params);
            return { items: response.items, total: response.total };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load more history';
            return rejectWithValue(message);
        }
    },
    {
        condition: (_, { getState }) => {
            const { loadingMore } = getState().researchHistory;
            return !loadingMore;
        },
    }
);

export const toggleResearchFavourite = createAsyncThunk<
    { nicheId: string | number; isFavourite: boolean },
    { nicheId: string | number; isFavourite: boolean },
    { rejectValue: string }
>(
    'researchHistory/toggleFavourite',
    async ({ nicheId, isFavourite }, { rejectWithValue }) => {
        try {
            await ResearchHistoryApiService.toggleFavourite(nicheId, isFavourite);
            return { nicheId, isFavourite };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to toggle favourite';
            return rejectWithValue(message);
        }
    }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const researchHistorySlice = createSlice({
    name: 'researchHistory',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<Partial<ResearchHistoryFilters>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetHistory: (state) => {
            state.items = [];
            state.total = 0;
            state.hasMore = true;
            state.loadingMore = false;
            state.error = null;
        },
        // Optimistic favourite toggle (before thunk resolves)
        // is_favourite is per-niche — find niche by nicheId across all items
        optimisticToggleFavourite: (state, action: PayloadAction<{ nicheId: string | number; isFavourite: boolean }>) => {
            for (const item of state.items) {
                const niche = item.niches.find(n => n.id === action.payload.nicheId);
                if (niche) { niche.is_favourite = action.payload.isFavourite; break; }
            }
        },
    },
    extraReducers: (builder) => {
        // ── fetchResearchHistory ──
        builder
            .addCase(fetchResearchHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchResearchHistory.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items;
                state.total = action.payload.total;
                state.hasMore = action.payload.items.length + 0 < action.payload.total;
            })
            .addCase(fetchResearchHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Failed to fetch research history';
            });

        // ── fetchMoreResearchHistory ──
        builder
            .addCase(fetchMoreResearchHistory.pending, (state) => {
                state.loadingMore = true;
                state.error = null;
            })
            .addCase(fetchMoreResearchHistory.fulfilled, (state, action) => {
                state.loadingMore = false;
                state.items = [...state.items, ...action.payload.items];
                state.total = action.payload.total;
                state.hasMore = state.items.length < action.payload.total;
            })
            .addCase(fetchMoreResearchHistory.rejected, (state, action) => {
                state.loadingMore = false;
                if (action.payload !== 'No more data') {
                    state.error = action.payload ?? 'Failed to load more';
                }
            });

        // ── toggleResearchFavourite ──
        builder
            .addCase(toggleResearchFavourite.rejected, (state, action) => {
                // Revert optimistic update on failure — requires re-fetch or reverting
                // For simplicity we just surface the error
                state.error = action.payload ?? 'Failed to toggle favourite';
            });
    },
});

export const { setFilters, resetHistory, optimisticToggleFavourite } = researchHistorySlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectResearchHistoryItems = (state: RootState) =>
    state.researchHistory.items.filter(item => item.niches.length > 0);
export const selectResearchHistoryLoading = (state: RootState) => state.researchHistory.loading;
export const selectResearchHistoryLoadingMore = (state: RootState) => state.researchHistory.loadingMore;
export const selectResearchHistoryError = (state: RootState) => state.researchHistory.error;
export const selectResearchHistoryHasMore = (state: RootState) => state.researchHistory.hasMore;
export const selectResearchHistoryTotal = (state: RootState) => state.researchHistory.total;
export const selectResearchHistoryFilters = (state: RootState) => state.researchHistory.filters;

export default researchHistorySlice.reducer;
